import * as React from 'react';
import { LogIn } from 'lucide-react';
import type {
  AgencyUserListItem,
  AgencyUserListResponse,
} from '@proptr/shared';
import { Card } from '../../components/ui/card.js';
import { cn, initials } from '../../lib/utils.js';
import { client } from '../../main.js';
import { useAgencyContext } from '../AgencyDetailPage.js';

// Where the manager portal lives. Vite sets `VITE_MANAGER_URL` per env;
// dev defaults to the local manager dev server.
const MANAGER_URL =
  (import.meta.env.VITE_MANAGER_URL as string | undefined) ??
  'http://localhost:5175';

export function AgencyUsersView() {
  const { enterpriseId, agency } = useAgencyContext();
  const [data, setData] = React.useState<AgencyUserListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enterpriseId) return;
    let alive = true;
    setError(null);
    setData(null);
    client.admin
      .listEnterpriseUsers(enterpriseId)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [enterpriseId]);

  // Prefer the loaded count; fall back to the parent agency doc so the
  // header doesn't flicker between page loads.
  const total = data?.meta.total ?? agency?.managersCount ?? 0;
  const items = data?.items ?? null;

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
        <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
          Users
        </h2>
        <p className="mt-2 font-sans text-[13px] leading-relaxed text-muted-foreground">
          <span className="text-foreground">{total}</span> manager
          {total === 1 ? '' : 's'} in this agency. Invite, disable, or reset
          manager passwords from here.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[hsl(var(--hairline))]">
              {['Manager', 'Email', 'Phone', 'Status', 'Joined', ''].map(
                (label, i) => (
                  <th
                    key={i}
                    className={cn(
                      'whitespace-nowrap px-5 py-3 text-left align-middle font-normal first:pl-5 last:pr-5',
                      i === 5 && 'text-right',
                    )}
                  >
                    <div className="mono-label text-muted-foreground">
                      {label}
                    </div>
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center font-mono text-[11px] text-destructive"
                >
                  {error}
                </td>
              </tr>
            ) : items === null ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground"
                >
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground"
                >
                  No managers found.
                </td>
              </tr>
            ) : (
              items.map((u) => <ManagerRow key={u._id} user={u} />)
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Mint a one-shot impersonation bearer token and open the manager portal in
// a new tab. The token rides on the URL once and is moved to sessionStorage
// by the manager bootstrap before the React tree mounts. The admin tab's
// `token` cookie is intentionally untouched so it keeps working.
async function loginAsManager(user: AgencyUserListItem): Promise<void> {
  const res = await client.auth.loginAs({ userId: user._id });
  const url = `${MANAGER_URL}?_imp=${encodeURIComponent(res.impToken)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function ManagerRow({ user }: { user: AgencyUserListItem }) {
  const name = user.name ?? user.email ?? '—';
  const statusName = user.status?.name ?? null;
  const isActive = user.status?.id === 1;
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const handleLoginAs = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await loginAsManager(user);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-[hsl(var(--hairline))] last:border-b-0 transition-colors hover:bg-muted/40">
      <Cell>
        <div className="flex items-center gap-3">
          <Avatar image={user.image} name={name} />
          <div className="whitespace-nowrap font-sans text-[14px] font-medium text-foreground">
            {name}
          </div>
        </div>
      </Cell>
      <Cell>
        <Mono value={user.email ?? '—'} dim={!user.email} />
      </Cell>
      <Cell>
        <Mono value={user.phone ?? '—'} dim={!user.phone} />
      </Cell>
      <Cell>
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              isActive ? 'bg-emerald-500' : 'bg-muted-foreground/40',
            )}
          />
          <span className="whitespace-nowrap font-mono text-[12px] font-medium text-foreground/80">
            {statusName ?? '—'}
          </span>
        </div>
      </Cell>
      <Cell>
        <Mono value={formatDate(user.createdAt)} dim={!user.createdAt} />
      </Cell>
      <Cell>
        <div className="flex items-center justify-end gap-3">
          {err && (
            <span
              className="font-mono text-[11px] text-destructive"
              title={err}
            >
              Failed
            </span>
          )}
          <button
            type="button"
            disabled={busy || !isActive}
            onClick={handleLoginAs}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.04em] text-foreground transition-colors',
              'hover:bg-muted hover:border-[hsl(var(--border-strong))]',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <LogIn className="h-3.5 w-3.5" />
            {busy ? 'Opening…' : 'Login as'}
          </button>
        </div>
      </Cell>
    </tr>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-5 py-4 align-middle first:pl-5 last:pr-5">{children}</td>
  );
}

function Mono({ value, dim }: { value: string; dim?: boolean }) {
  return (
    <div
      className={cn(
        'whitespace-nowrap font-mono text-[12px] font-medium tabular-nums text-foreground/80',
        dim && 'text-muted-foreground',
      )}
    >
      {value}
    </div>
  );
}

function Avatar({ image, name }: { image: string | null; name: string }) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-9 w-9 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
      {initials(name)}
    </div>
  );
}

function formatDate(d: string | Date | null): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
